import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SendIcon, BotIcon, UserIcon, SparklesIcon, LightbulbIcon, BrainIcon, PencilRulerIcon, CodeIcon } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { getGradientClasses, getAccentColor } from '../../utils/helpers';
import { GeometricPattern } from '../../utils/patterns';
interface Message {
  id: string;
  content: string;
  sender: 'user' | 'assistant';
  timestamp: Date;
}
export function AIAssistantApp() {
  const {
    getThemeClasses,
    theme,
    t
  } = useTheme();
  const themeClasses = getThemeClasses();
  const gradients = getGradientClasses(theme);
  const accentColor = getAccentColor(theme);
  const [messages, setMessages] = useState<{
    id: string;
    content: string;
    sender: 'user' | 'assistant';
    timestamp: Date;
  }[]>([{
    id: '1',
    content: t.aiAssistant?.greeting || 'Hallo! Ik ben je AI-assistent. Hoe kan ik je helpen MVL Creative te verkennen?',
    sender: 'assistant',
    timestamp: new Date()
  }]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [messageBeingRead, setMessageBeingRead] = useState<string | null>(null);
  const [messageReactions, setMessageReactions] = useState<Record<string, string>>({});
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim()) return;
    // Add user message
    const userMessage = {
      id: Date.now().toString(),
      content: inputValue,
      sender: 'user' as const,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    // Simulate AI typing
    setIsTyping(true);
    setTimeout(() => {
      // Add AI response
      const aiMessage = {
        id: (Date.now() + 1).toString(),
        content: t.aiAssistant?.simulatedResponse || 'Dit is een gesimuleerd antwoord. In een productieomgeving zou dit verbinding maken met een AI API voor intelligente antwoorden.',
        sender: 'assistant' as const,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiMessage]);
      setIsTyping(false);
    }, 1500);
  };
  // Add reaction to message
  const addReaction = (messageId: string, reaction: string) => {
    setMessageReactions(prev => ({
      ...prev,
      [messageId]: reaction
    }));
  };
  return <div className={`flex flex-col h-full ${themeClasses.text}`} data-id="element-53">
      {/* Header with refined geometric pattern */}
      <div className={`p-6 md:p-8 border-b ${themeClasses.border} relative overflow-hidden`} data-id="element-54">
        <div className="absolute inset-0 opacity-[0.02]" data-id="element-55">
          <GeometricPattern pattern="dots" size="small" data-id="element-56" />
        </div>
        <div className="relative z-10" data-id="element-57">
          <div className="flex items-center gap-4 mb-4" data-id="element-58">
            <div className={`w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-violet-500 flex items-center justify-center relative overflow-hidden shadow-lg`} style={{
            boxShadow: '0 10px 25px -5px rgba(139, 92, 246, 0.5)'
          }} data-id="element-59">
              <GeometricPattern pattern="star" size="medium" className="opacity-30" data-id="element-60" />
              <SparklesIcon className="w-6 h-6 text-white relative z-10" data-id="element-61" />
            </div>
            <div data-id="element-62">
              <h1 className="text-2xl font-bold mb-1" data-id="element-63">
                {t.aiAssistant?.title || 'AI Assistent'}
              </h1>
              <p className="text-sm opacity-60" data-id="element-64">
                {t.aiAssistant?.subtitle || 'Altijd hier om te helpen'}
              </p>
            </div>
          </div>
          <p className={`${themeClasses.text} opacity-80 leading-relaxed max-w-3xl`} data-id="element-65">
            Stel vragen over mijn expertise, projecten of werkwijze. Ik kan je
            helpen de juiste informatie te vinden en uitleggen hoe ik complexe
            vragen vertaal naar elegante oplossingen.
          </p>
        </div>
      </div>
      {/* Enhanced chat messages with reactions */}
      <div className={`flex-1 p-6 overflow-y-auto ${themeClasses.card} space-y-6`} data-id="element-66">
        {messages.map(message => <motion.div key={message.id} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`} onHoverStart={() => setMessageBeingRead(message.id)} onHoverEnd={() => setMessageBeingRead(null)} data-id="element-67">
            <div className={`max-w-[80%] md:max-w-[70%] flex gap-3 ${message.sender === 'user' ? 'flex-row-reverse' : ''}`} data-id="element-68">
              {/* Avatar with pulse animation for assistant */}
              <div className={`w-10 h-10 rounded-lg flex-shrink-0 ${message.sender === 'assistant' ? 'bg-gradient-to-br from-purple-500 to-violet-500 relative' : `${themeClasses.card} border ${themeClasses.border}`} flex items-center justify-center relative overflow-hidden`} style={message.sender === 'assistant' ? {
            boxShadow: '0 8px 16px -4px rgba(139, 92, 246, 0.4)'
          } : {}} data-id="element-69">
                {message.sender === 'assistant' && messageBeingRead === message.id && <motion.div className="absolute inset-0 bg-white/20 rounded-lg" animate={{
              scale: [1, 1.2, 1],
              opacity: [0.1, 0.2, 0.1]
            }} transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-70" />}
                <GeometricPattern pattern={message.sender === 'assistant' ? 'circles' : 'square-rotate'} size="small" className="opacity-30" data-id="element-71" />
                {message.sender === 'assistant' ? <BotIcon className="w-5 h-5 text-white relative z-10" aria-hidden="true" data-id="element-72" /> : <UserIcon className={`w-5 h-5 ${themeClasses.icon} relative z-10`} aria-hidden="true" data-id="element-73" />}
              </div>
              {/* Message bubble with reaction options */}
              <div className="relative" data-id="element-74">
                <div className={`p-4 rounded-2xl relative ${message.sender === 'assistant' ? `${themeClasses.card} border ${themeClasses.border}` : 'bg-gradient-to-br from-purple-500 to-violet-500 text-white'}`} style={message.sender === 'user' ? {
              boxShadow: '0 8px 16px -4px rgba(139, 92, 246, 0.4)'
            } : {}} data-id="element-75">
                  {message.sender === 'user' && <div className="absolute inset-0 opacity-10" data-id="element-76">
                      <GeometricPattern pattern="dots" size="small" data-id="element-77" />
                    </div>}
                  <p className="relative z-10" data-id="element-78">{message.content}</p>
                  <div className={`text-[10px] mt-2 ${message.sender === 'assistant' ? `${themeClasses.text} opacity-50` : 'text-white/70'} text-right`} data-id="element-79">
                    {message.timestamp.toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                  </div>
                </div>
                {/* Reaction indicator */}
                {messageReactions[message.id] && <motion.div initial={{
              scale: 0,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} className={`absolute ${message.sender === 'user' ? 'left-0 -translate-x-1/2' : 'right-0 translate-x-1/2'} bottom-0 translate-y-1/3 w-6 h-6 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white text-xs shadow-lg`} data-id="element-80">
                    {messageReactions[message.id]}
                  </motion.div>}
                {/* Reaction options on hover */}
                {messageBeingRead === message.id && message.sender === 'assistant' && <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="absolute -bottom-8 left-0 flex items-center gap-1 p-1 rounded-full bg-white/10 backdrop-blur-lg border border-white/20" data-id="element-81">
                      {['👍', '❤️', '😊', '🙏'].map(reaction => <motion.button key={reaction} onClick={() => addReaction(message.id, reaction)} className="w-6 h-6 rounded-full hover:bg-white/20 flex items-center justify-center transition-colors" whileHover={{
                scale: 1.2
              }} whileTap={{
                scale: 0.9
              }} data-id="element-82">
                          {reaction}
                        </motion.button>)}
                    </motion.div>}
              </div>
            </div>
          </motion.div>)}
        {/* Enhanced typing indicator with dynamic dots */}
        {isTyping && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex justify-start" data-id="element-83">
            <div className="max-w-[80%] md:max-w-[70%] flex gap-3" data-id="element-84">
              <div className="w-10 h-10 rounded-lg flex-shrink-0 bg-gradient-to-br from-purple-500 to-violet-500 flex items-center justify-center relative overflow-hidden" style={{
            boxShadow: '0 8px 16px -4px rgba(139, 92, 246, 0.4)'
          }} data-id="element-85">
                <GeometricPattern pattern="circles" size="small" className="opacity-30" data-id="element-86" />
                <motion.div animate={{
              rotate: [0, 360],
              scale: [1, 1.1, 1]
            }} transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'linear'
            }} data-id="element-87">
                  <BotIcon className="w-5 h-5 text-white relative z-10" aria-hidden="true" data-id="element-88" />
                </motion.div>
              </div>
              <div className={`p-4 rounded-2xl ${themeClasses.card} border ${themeClasses.border}`} data-id="element-89">
                <div className="flex space-x-2" data-id="element-90">
                  {[0, 1, 2].map(i => <motion.div key={i} className="w-2 h-2 rounded-full bg-purple-500" animate={{
                y: ['0%', '-50%', '0%'],
                opacity: [0.5, 1, 0.5]
              }} transition={{
                duration: 1,
                repeat: Infinity,
                delay: i * 0.2,
                ease: 'easeInOut'
              }} data-id="element-91" />)}
                </div>
              </div>
            </div>
          </motion.div>}
        <div ref={messagesEndRef} data-id="element-92" />
      </div>
      {/* Quick suggestions with refined patterns */}
      <div className={`px-6 py-4 border-t ${themeClasses.border}`} data-id="element-93">
        <p className={`text-xs ${themeClasses.text} opacity-50 mb-3`} data-id="element-94">
          Suggesties:
        </p>
        <div className="flex flex-wrap gap-2 mb-4" data-id="element-95">
          {[{
          text: 'Vertel me over je expertise',
          icon: BrainIcon,
          color: 'from-blue-500 to-cyan-500',
          pattern: 'square-rotate' as const
        }, {
          text: 'Hoe vertaal je complexe vragen?',
          icon: LightbulbIcon,
          color: 'from-amber-500 to-orange-500',
          pattern: 'diamond' as const
        }, {
          text: 'Wat is je designproces?',
          icon: PencilRulerIcon,
          color: 'from-pink-500 to-rose-500',
          pattern: 'triangles' as const
        }, {
          text: 'Welke tools gebruik je?',
          icon: CodeIcon,
          color: 'from-emerald-500 to-teal-500',
          pattern: 'circles' as const
        }].map((suggestion, index) => {
          const Icon = suggestion.icon;
          return <button key={index} onClick={() => setInputValue(suggestion.text)} className={`px-4 py-2 rounded-xl ${themeClasses.card} border ${themeClasses.border} text-sm flex items-center gap-2 hover:scale-105 transition-transform relative overflow-hidden group`} data-id="element-96">
                <div className={`w-6 h-6 rounded-lg bg-gradient-to-br ${suggestion.color} flex items-center justify-center relative overflow-hidden flex-shrink-0`} style={{
              boxShadow: `0 4px 8px -2px rgba(${suggestion.color.includes('blue') ? '59, 130, 246' : suggestion.color.includes('amber') ? '245, 158, 11' : suggestion.color.includes('pink') ? '236, 72, 153' : '16, 185, 129'}, 0.4)`
            }} data-id="element-97">
                  <GeometricPattern pattern={suggestion.pattern} size="small" className="opacity-30" data-id="element-98" />
                  <Icon className="w-3 h-3 text-white relative z-10" data-id="element-99" />
                </div>
                <span data-id="element-100">{suggestion.text}</span>
              </button>;
        })}
        </div>
      </div>
      {/* Enhanced input form with visual feedback */}
      <form onSubmit={handleSendMessage} className={`p-4 border-t ${themeClasses.border} flex gap-3`} data-id="element-101">
        <div className={`flex-1 ${themeClasses.card} border ${themeClasses.border} rounded-xl overflow-hidden flex items-center px-4 focus-within:border-purple-500 transition-colors relative`} data-id="element-102">
          <motion.div className="absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b from-purple-500 to-violet-500 opacity-50 rounded-l-xl" initial={{
          height: '0%'
        }} animate={{
          height: inputValue.length > 0 ? '100%' : '30%',
          opacity: inputValue.length > 0 ? 0.8 : 0.3
        }} transition={{
          duration: 0.3
        }} data-id="element-103" />
          <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} placeholder={t.aiAssistant?.placeholder || 'Stel een vraag...'} className={`py-3 px-2 bg-transparent outline-none flex-1 ${themeClasses.text}`} disabled={isTyping} data-id="element-104" />
          {inputValue.length > 0 && <motion.div initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 0.5
        }} className={`mr-2 text-xs ${themeClasses.text}`} data-id="element-105">
              {inputValue.length} / 280
            </motion.div>}
        </div>
        <motion.button type="submit" disabled={!inputValue.trim() || isTyping} className={`p-3 rounded-xl bg-gradient-to-br from-purple-500 to-violet-500 text-white ${!inputValue.trim() || isTyping ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'} transition-all relative overflow-hidden`} style={{
        boxShadow: '0 8px 16px -4px rgba(139, 92, 246, 0.4)'
      }} whileHover={{
        scale: inputValue.trim() && !isTyping ? 1.05 : 1
      }} whileTap={{
        scale: inputValue.trim() && !isTyping ? 0.95 : 1
      }} animate={{
        y: inputValue.trim() && !isTyping ? [0, -2, 0] : 0
      }} transition={{
        y: {
          duration: 1.5,
          repeat: inputValue.trim() && !isTyping ? Infinity : 0,
          repeatType: 'reverse'
        }
      }} data-id="element-106">
          <GeometricPattern pattern="circles" size="small" className="opacity-30" data-id="element-107" />
          <SendIcon className="w-5 h-5 text-white relative z-10" data-id="element-108" />
        </motion.button>
      </form>
    </div>;
}